import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import FleetMap from "@/components/FleetMap";
import { Link } from "react-router-dom";
import {
    Zap, Sun, Battery, AlertTriangle, Activity, Building2, TreePine, Leaf, Gauge,
    ChevronDown, ChevronRight, TrendingDown, Ticket, Clock, Users, Timer,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Real-time overview refresh — keep console honest without hammering the API
const OVERVIEW_REFRESH_MS = 60000;

// Auto-switch generation units at 1000 kWh (1 MWh)
function formatEnergy(kwh) {
    if (kwh == null) return { value: "0", unit: "kWh" };
    if (kwh < 1000) return { value: kwh.toLocaleString(undefined, { maximumFractionDigits: 1 }), unit: "kWh" };
    return { value: (kwh / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }), unit: "MWh" };
}

export default function Dashboard() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [ov, setOv] = useState(null);
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        const load = () => Promise.all([api.get("/fleet/overview"), api.get("/plants")])
            .then(([o, p]) => { setOv(o.data); setPlants(p.data); setErr(null); })
            .catch((e) => { setErr(e?.response?.data?.detail || e?.message || "Fleet telemetry unavailable"); })
            .finally(() => setLoading(false));
        load();
        const t = setInterval(load, OVERVIEW_REFRESH_MS);
        return () => clearInterval(t);
    }, []);

    if (loading) return <div className="text-zinc-500 text-sm" data-testid="dashboard-loading">Loading fleet telemetry…</div>;

    // Backend occasionally returns 500 when SolarOS upstream rate-limits
    // or 403s. Surface a friendly empty-state instead of crashing on `ov.*`.
    if (!ov) {
        return (
            <div className="space-y-4" data-testid="dashboard-error">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Fleet Overview</div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900">Command Console</h1>
                <div className="bg-white border border-zinc-200 rounded-2xl p-8 flex flex-col items-start gap-4 max-w-xl">
                    <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                        <div className="text-base font-semibold text-zinc-900">Live telemetry is temporarily unavailable</div>
                        <div className="text-sm text-zinc-600 mt-1 leading-relaxed">
                            The SolarOS feed didn't respond on this cycle. The console will retry automatically every minute — recent data remains cached and nothing was lost.
                        </div>
                        {err && <div className="text-[11px] text-zinc-400 font-mono mt-3 break-all">{String(err).slice(0, 220)}</div>}
                    </div>
                    <button
                        onClick={() => { setLoading(true); setErr(null); Promise.all([api.get("/fleet/overview"), api.get("/plants")]).then(([o,p])=>{setOv(o.data);setPlants(p.data);}).catch((e)=>setErr(e?.response?.data?.detail||e?.message||"Fleet telemetry unavailable")).finally(()=>setLoading(false)); }}
                        className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10 px-5 text-sm font-semibold"
                        data-testid="dashboard-retry"
                    >
                        Retry now
                    </button>
                </div>
            </div>
        );
    }

    const critical = ov?.critical_systems || [];
    const warning = ov?.warning_systems || [];
    const perfDrop = ov?.perf_drop_systems || [];
    const openTickets = ov?.open_tickets || [];
    const healthy = plants.length - critical.length - warning.length - perfDrop.length;

    const todayEnergy = formatEnergy(ov?.todays_generation_kwh);
    const lifetimeEnergy = formatEnergy(ov?.lifetime_kwh);

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Fleet Overview</div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">Command Console</h1>
                </div>
                <div className="text-xs text-zinc-500 font-mono flex items-center gap-2 flex-shrink-0">
                    <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
                    {ov?.data_source === "mock" ? "SIMULATED FEED" : "LIVE · SolarOS"}
                </div>
            </div>

            {/* Sustainability Hero */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="sustainability-hero">
                <HeroCard
                    icon={Gauge}
                    label="Total Fleet Capacity"
                    value={(ov.total_installed_capacity_kw ?? 0).toLocaleString()}
                    unit="kWp"
                    sub={`across ${ov.total_plants} connected system${ov.total_plants !== 1 ? "s" : ""}`}
                    testid="hero-fleet-capacity"
                />
                <HeroCard
                    icon={Leaf}
                    label="Total CO₂ Saved"
                    value={((ov.co2_saved_kg ?? 0) / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    unit="tonnes"
                    sub={`from ${lifetimeEnergy.value} ${lifetimeEnergy.unit} clean energy · 0.7 kg/kWh`}
                    testid="hero-co2-saved"
                />
                <HeroCard
                    icon={TreePine}
                    label="Trees Planted Equivalent"
                    value={(ov.trees_equivalent ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    unit="trees"
                    sub="20 kg CO₂ absorbed per tree annually"
                    testid="hero-trees"
                />
            </div>

            {/* Executive KPI band — three operational metrics that complement
                (not duplicate) the Hero sustainability band above and the
                6-metric KPI grid below. */}
            {isSuperAdmin && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="executive-kpi-band">
                    <ExecCard
                        icon={Users}
                        label="Active Users"
                        value={ov.active_users_count ?? 0}
                        sub={`Operators + customers on the console`}
                        testid="exec-active-users"
                    />
                    <ExecCard
                        icon={Activity}
                        label="Active Plants"
                        value={`${ov.plants_online ?? 0} / ${ov.total_plants ?? 0}`}
                        sub={ov.plants_online === ov.total_plants && ov.total_plants > 0
                            ? "All systems reporting live telemetry"
                            : `${(ov.total_plants ?? 0) - (ov.plants_online ?? 0)} offline — awaiting reconnect`}
                        testid="exec-active-plants"
                    />
                    <ExecCard
                        icon={Ticket}
                        label="Open Tickets"
                        value={(ov.open_tickets || []).length}
                        sub={ov.stale_tickets_count > 0
                            ? `${ov.stale_tickets_count} open longer than ${ov.stale_threshold_hours ?? 48}h`
                            : "All caught up — nothing stale"}
                        testid="exec-open-tickets"
                        tone={ov.stale_tickets_count > 0 ? "red" : "black"}
                    />
                </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="kpi-grid">
                <KpiCard label="Total Plants" value={ov.total_plants} accent="black" icon={Building2} testid="kpi-total-plants" />
                <KpiCard label="Live Power" value={ov.live_power_kw.toFixed(1)} unit="kW" accent="black" icon={Zap} testid="kpi-live-power" />
                <KpiCard label="Today's Gen" value={todayEnergy.value} unit={todayEnergy.unit} accent="black" icon={Sun} testid="kpi-today-gen" />
                <KpiCard label="Lifetime" value={lifetimeEnergy.value} unit={lifetimeEnergy.unit} accent="black" icon={Battery} testid="kpi-lifetime" />
                <KpiCard label="Active Alarms" value={ov.active_alarms} accent={ov.active_alarms > 0 ? "orange" : "black"} icon={AlertTriangle} testid="kpi-alarms" />
                <KpiCard label="Online / Total" value={`${ov.plants_online}/${ov.total_plants}`} accent="black" icon={Activity} testid="kpi-online" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-2xl">
                    <div className="p-5 border-b border-zinc-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Fleet Map</div>
                            <div className="text-sm text-zinc-700 mt-1">All installations across India</div>
                        </div>
                        <div className="flex items-center gap-3 text-[11px] font-mono flex-wrap">
                            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#16A34A]" /> Healthy</span>
                            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#F97316]" /> Warning</span>
                            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Critical</span>
                        </div>
                    </div>
                    <div className="p-5"><FleetMap plants={plants} /></div>
                </div>

                <div className="bg-white border border-zinc-200 rounded-2xl" data-testid="fleet-health-card">
                    <div className="p-5 border-b border-zinc-100">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Fleet Health</div>
                        <div className="text-sm text-zinc-700 mt-1">Snapshot · click any category to see affected systems</div>
                    </div>
                    <div className="p-5 space-y-3">
                        <HealthGroup
                            label="Critical"
                            icon={AlertTriangle}
                            color="#EF4444"
                            systems={critical}
                            total={plants.length}
                            testid="health-critical"
                            reasonLabel="Critical alert raised"
                        />
                        <HealthGroup
                            label="Warning"
                            icon={AlertTriangle}
                            color="#F97316"
                            systems={warning}
                            total={plants.length}
                            testid="health-warning"
                            reasonLabel="Active alert (non-critical)"
                        />
                        <HealthGroup
                            label="Warning · <50% Expected"
                            icon={TrendingDown}
                            color="#EAB308"
                            systems={perfDrop}
                            total={plants.length}
                            testid="health-perf-drop"
                            reasonLabel="Under 50% of expected output"
                            showPerf
                        />
                        <HealthRow label="Healthy" value={Math.max(0, healthy)} total={plants.length} color="#16A34A" />
                        <Link to="/service-required" className="mt-4 block text-center py-3 bg-[#0A0A0A] text-white text-sm font-semibold rounded-full hover:bg-black transition-colors duration-200" data-testid="goto-service">
                            View service queue →
                        </Link>
                    </div>
                </div>
            </div>

            {/* Live Tickets — open service tickets across the fleet */}
            <LiveTickets tickets={openTickets} />

            <div className="bg-white border border-zinc-200 rounded-2xl">
                <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Recent Plants</div>
                        <div className="text-sm text-zinc-700 mt-1">Top installations by capacity</div>
                    </div>
                    <Link to="/plants" className="text-sm text-zinc-900 hover:text-black font-semibold" data-testid="view-all-plants">View all →</Link>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]" data-testid="recent-plants-table">
                    <thead>
                        <tr className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-y border-zinc-200">
                            <th className="py-2.5 px-4 text-left font-semibold">Plant</th>
                            <th className="py-2.5 px-4 text-left font-semibold">Capacity</th>
                            <th className="py-2.5 px-4 text-left font-semibold">Live</th>
                            <th className="py-2.5 px-4 text-left font-semibold">Health</th>
                            <th className="py-2.5 px-4 text-left font-semibold">Customer</th>
                        </tr>
                    </thead>
                    <tbody>
                        {plants.slice(0, 8).map((p, i) => (
                            <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200" data-testid={`plant-row-${i}`}>
                                <td className="py-3 px-4">
                                    <Link to={`/plants/${p.id}`} className="text-zinc-900 hover:text-[#0A0A0A] font-medium">
                                        {p.customer_name || p.name}
                                    </Link>
                                    {p.customer_name && p.location && (
                                        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{p.location}</div>
                                    )}
                                </td>
                                <td className="py-3 px-4 font-mono text-zinc-700">{p.installed_capacity_kw} kWp</td>
                                <td className="py-3 px-4 font-mono text-zinc-700">{p.current_power_kw?.toFixed(1)} kW</td>
                                <td className="py-3 px-4"><StatusBadge health={p.health} /></td>
                                <td className="py-3 px-4 text-zinc-600">{p.owner_name}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}

function HealthGroup({ label, icon: Icon, color, systems, total, testid, reasonLabel, showPerf }) {
    const [open, setOpen] = useState(false);
    const pct = total ? (systems.length / total) * 100 : 0;
    const hasSystems = systems.length > 0;

    return (
        <div data-testid={testid}>
            <button
                onClick={() => hasSystems && setOpen(o => !o)}
                disabled={!hasSystems}
                className={`w-full text-left ${hasSystems ? "cursor-pointer hover:bg-zinc-50" : "cursor-default"} rounded-sm py-1 px-1 transition-colors`}
                data-testid={`${testid}-toggle`}
            >
                <div className="flex items-baseline justify-between mb-1">
                    <div className="text-sm text-zinc-700 flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                        {label}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="text-sm font-mono text-zinc-900">
                            <span className="text-lg font-medium" style={{ color }}>{systems.length}</span>
                            <span className="text-zinc-400"> / {total}</span>
                        </div>
                        {hasSystems && (open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />)}
                    </div>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
                </div>
            </button>
            {open && hasSystems && (
                <div className="mt-2 ml-1 space-y-1 pl-2 border-l-2" style={{ borderColor: color }} data-testid={`${testid}-list`}>
                    {systems.map((s, i) => (
                        <Link
                            key={s.station_id}
                            to={`/plants/${s.station_id}`}
                            className="flex items-center justify-between text-xs hover:bg-zinc-50 rounded-sm px-2 py-1.5 group"
                            data-testid={`${testid}-row-${i}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="text-zinc-900 font-medium group-hover:text-[#0A0A0A] truncate">
                                    {s.customer_name || s.plant_name}
                                </div>
                                <div className="text-[10px] font-mono text-zinc-500 truncate">
                                    {s.location && `${s.location} · `}{reasonLabel}
                                    {showPerf && s.performance_drop_pct != null && (
                                        <> · <span className="text-[#EAB308] font-semibold">{s.performance_drop_pct}% below expected</span></>
                                    )}
                                </div>
                            </div>
                            <ChevronRight className="w-3 h-3 text-zinc-400 ml-2 group-hover:text-[#0A0A0A] flex-shrink-0" />
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function LiveTickets({ tickets }) {
    return (
        <div className="bg-white border border-zinc-200 rounded-2xl" data-testid="live-tickets-card">
            <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-zinc-700" />
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Live Tickets</div>
                        <div className="text-sm text-zinc-700 mt-1">
                            {tickets.length === 0 ? "All clear — no open tickets." : `${tickets.length} open ticket${tickets.length === 1 ? "" : "s"} across the fleet`}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-[11px] font-mono">
                    <span className="w-2 h-2 rounded-full bg-[#DC2626] animate-pulse" />
                    <span className="text-[#DC2626]">LIVE</span>
                </div>
            </div>
            {tickets.length === 0 ? (
                <div className="p-6 text-sm text-zinc-500 text-center" data-testid="live-tickets-empty">
                    Every plant is happy right now. Raise a ticket from any Plant page when something needs attention.
                </div>
            ) : (
                <table className="w-full text-sm" data-testid="live-tickets-table">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
                            <th className="py-2 px-4 text-left font-semibold">Ticket #</th>
                            <th className="py-2 px-4 text-left font-semibold">Plant</th>
                            <th className="py-2 px-4 text-left font-semibold">Subject</th>
                            <th className="py-2 px-4 text-left font-semibold">Priority</th>
                            <th className="py-2 px-4 text-left font-semibold">Age</th>
                            <th className="py-2 px-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tickets.slice(0, 10).map((t, i) => {
                            const priTone = t.priority === "high" ? "text-[#DC2626] bg-red-50 border-red-200" : t.priority === "low" ? "text-zinc-600 bg-zinc-50 border-zinc-200" : "text-blue-700 bg-blue-50 border-blue-200";
                            return (
                                <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`live-ticket-row-${i}`}>
                                    <td className="py-2.5 px-4 font-mono text-xs text-zinc-800">{t.ticket_number}</td>
                                    <td className="py-2.5 px-4">
                                        <Link to={`/plants/${t.station_id}`} className="text-zinc-900 font-medium hover:text-[#0A0A0A]">
                                            {t.customer_name || t.plant_name}
                                        </Link>
                                        {t.location && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{t.location}</div>}
                                    </td>
                                    <td className="py-2.5 px-4 text-zinc-700 truncate max-w-xs">{t.subject}</td>
                                    <td className="py-2.5 px-4">
                                        <span className={`inline-block text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${priTone}`}>
                                            {(t.priority || "normal").toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="py-2.5 px-4 font-mono text-xs">
                                        <span className="inline-flex items-center gap-1 text-[#DC2626]">
                                            <Clock className="w-3 h-3" /> {t.days_open}d
                                        </span>
                                    </td>
                                    <td className="py-2.5 px-4 text-right">
                                        <Link to={`/plants/${t.station_id}`} className="text-[#0A0A0A] text-xs font-medium hover:underline">Open →</Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function HealthRow({ label, value, total, color }) {
    const pct = total ? (value / total) * 100 : 0;
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1">
                <div className="text-sm text-zinc-700">{label}</div>
                <div className="text-sm font-mono text-zinc-900"><span className="text-lg font-medium" style={{ color }}>{value}</span> / {total}</div>
            </div>
            <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
}

function HeroCard({ icon: Icon, label, value, unit, sub, testid }) {
    return (
        <div
            className="relative overflow-hidden rounded-2xl bg-white border border-zinc-200 p-6 min-h-[200px] flex flex-col hover:border-zinc-300 transition-colors"
            data-testid={testid}
        >
            <div className="flex items-start justify-between mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
                <div className="w-9 h-9 rounded-full bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-white" strokeWidth={2} />
                </div>
            </div>
            <div className="flex items-baseline gap-2">
                <div className="text-5xl font-dot text-zinc-900 leading-none tracking-tight">{value}</div>
                {unit && <div className="text-sm font-mono text-zinc-500">{unit}</div>}
            </div>
            {sub && <div className="text-[11px] text-zinc-500 mt-auto pt-3 font-mono">{sub}</div>}
        </div>
    );
}

function ExecCard({ icon: Icon, label, value, unit, sub, testid, tone = "black" }) {
    const toneClasses = {
        black: "border-zinc-200",
        red: "border-red-200 bg-red-50/40",
        green: "border-emerald-200 bg-emerald-50/40",
    };
    const valueTone = tone === "red" ? "text-red-700" : tone === "green" ? "text-emerald-700" : "text-zinc-900";
    return (
        <div className={`bg-white border rounded-2xl px-6 py-5 min-h-[160px] flex flex-col hover:border-zinc-300 transition-colors ${toneClasses[tone] || toneClasses.black}`} data-testid={testid}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
                    <div className="flex items-baseline gap-1.5 mt-2">
                        <span className={`text-5xl font-dot tabular-nums tracking-tight leading-none ${valueTone}`}>{value}</span>
                        {unit && <span className="text-xs text-zinc-500 font-mono">{unit}</span>}
                    </div>
                </div>
                <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-zinc-700" />
                </div>
            </div>
            {sub && <div className="text-[11px] text-zinc-500 mt-auto pt-3">{sub}</div>}
        </div>
    );
}

